import {TokenUtil} from "../../app/utils/Utils";
import {HTTPMethod} from "../enums/HTTPMethod";
import {ConnectionError, FetchRequestError, NonJsonResponseError} from "../exceptions/CustomError";
import {IRequestConfig} from "../models/IRequestConfig";

/**
 * DefaultRequestConfig implementa a interface IRequestConfig, fornecendo uma configuração padrão.
 */
class DefaultRequestConfig implements IRequestConfig {
    getHeaders(): HeadersInit {
        return {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${TokenUtil.getToken()}`
        };
    }
}

/**
 * ApiRequestHandler fornece uma classe utilitária estática para realizar requisições HTTP
 * usando a Fetch API, com suporte integrado para métodos REST comuns, como GET, POST
 * e PUT. Simplifica a inclusão de cabeçalhos padrão e personalizados, incluindo autorização,
 * e oferece uma maneira estruturada de tratar respostas e erros.
 */
export class ApiRequestHandler {
    /**
     * Realiza uma requisição GET.
     * @param input A URL ou objeto RequestInfo para a requisição.
     * @param queryParams Parâmetros de consulta opcionais para serem anexados à URL.
     * @param config Parâmetro opcional que permite a customização dos cabeçalhos da requisição.
     * @returns Uma Promise que resolve para a resposta processada como JSON.
     */
    static async get(input: RequestInfo | URL, queryParams?: Record<string, any>, config?: IRequestConfig) {
        const url = new URL(input.toString());
        if (queryParams) {
            Object.keys(queryParams).forEach(key => url.searchParams.append(key, queryParams[key]));
        }
        return await this.fetch(HTTPMethod.GET, url, null, config);
    }

    /**
     * Realiza uma requisição POST.
     * @param input A URL ou objeto RequestInfo para a requisição.
     * @param init Corpo opcional da requisição.
     * @param headers Cabeçalhos personalizados opcionais para incluir na requisição.
     * @param config Parâmetro opcional que permite a customização dos cabeçalhos da requisição.
     * @returns Uma Promise que resolve para a resposta processada como JSON.
     */
    static async post(input: RequestInfo | URL, init?: BodyInit | null, config?: IRequestConfig) {
        return await this.fetch(HTTPMethod.POST, input, init, config);
    }

    /**
     * Realiza uma requisição PUT.
     * @param input A URL ou objeto RequestInfo para a requisição.
     * @param init Corpo opcional da requisição.
     * @param headers Cabeçalhos personalizados opcionais para incluir na requisição.
     * @param config Parâmetro opcional que permite a customização dos cabeçalhos da requisição.
     * @returns Uma Promise que resolve para a resposta processada como JSON.
     */
    static async put(input: RequestInfo | URL, init?: BodyInit | null, config?: IRequestConfig) {
        return await this.fetch(HTTPMethod.PUT, input, init, config);
    }

    /**
     * Uma utilidade de fetch genérica que é usada pelos métodos GET, POST e PUT para
     * realizar as requisições reais. Inclui cabeçalhos padrão para JSON e autorização.
     * @param method O método HTTP a ser usado.
     * @param input A URL ou objeto RequestInfo para a requisição.
     * @param body Corpo opcional da requisição para os métodos POST e PUT.
     * @param config Parâmetro opcional que permite a customização dos cabeçalhos da requisição.
     * @returns Uma Promise que resolve para a resposta processada como JSON.
     */
    static async fetch(method: HTTPMethod, input: RequestInfo | URL, body?: BodyInit | null, config?: IRequestConfig) {
        const headers = new Headers(config?.getHeaders() ?? new DefaultRequestConfig().getHeaders());

        const init: RequestInit = {method, headers, ...(body ? {body} : {})};

        try {
            const response = await fetch(input, init);

            if (!response.ok) {
                const errorBody = await response.text();
                //throw new Error(`Request failed with status ${response.status}: ${errorBody}`);
                throw new FetchRequestError(response.status, errorBody);
            }

            // Verificação adicional para garantir que a resposta é JSON antes de tentar parseá-la
            const contentType = response.headers.get('Content-Type');
            if (!contentType || !contentType.includes('application/json')) {
                //throw new Error('O tipo de conteúdo da resposta não é JSON');
                throw new NonJsonResponseError(response.status);
            }

            return await response.json(); // Poderia retornar response.json() para tratar a resposta como JSON. Avaliar no futuro.
        } catch (error: unknown) {
            // Tratamento de erros de rede
            if (error instanceof FetchRequestError && error.status === 401) {
                throw new Error('Não é possível atender à solicitação neste momento. Solicitamos que tente novamente posteriormente.');
            }
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                throw new ConnectionError();
            }
            throw error; // Repassar o erro pode ser útil para tratamento adicional pelo chamador
        }
    }

   /**
     * Realiza uma requisição GET voltada para o download de arquivos (Blob).
     * @param input A URL ou objeto RequestInfo para a requisição.
     * @param queryParams Parâmetros de consulta opcionais.
     * @param config Parâmetro opcional para customização de cabeçalhos.
     * @returns Uma Promise que resolve para um objeto contendo o Blob e o nome do arquivo.
     */
    static async getBlob(input: RequestInfo | URL, queryParams?: Record<string, any>, config?: IRequestConfig): Promise<{ blob: Blob, filename: string }> {
        const url = new URL(input.toString());
        
        if (queryParams) {
            // Convertemos o valor explicitamente para String para o append do searchParams não reclamar
            Object.keys(queryParams).forEach(key => {
                if (queryParams[key] !== undefined && queryParams[key] !== null) {
                    url.searchParams.append(key, String(queryParams[key]));
                }
            });
        }

        // Pega os headers padrões (incluindo o Bearer token)
        const headers = new Headers(config?.getHeaders() ?? new DefaultRequestConfig().getHeaders());
        
        // Removemos a exigência estrita de JSON e aceitamos arquivos
        headers.set('Accept', 'application/pdf, application/octet-stream, */*');

        const init: RequestInit = { method: HTTPMethod.GET, headers };

        try {
            const response = await fetch(url, init);

            if (response.status === 204) {
                // Lança um erro para que o catch do seu componente exiba um aviso na tela
                throw new Error('Nenhum documento encontrado para o período selecionado.');
            }

            if (!response.ok) {
                const errorBody = await response.text();
                throw new FetchRequestError(response.status, errorBody);
            }

            const blob = await response.blob();
            
            // Fallback genérico caso o back-end não envie o header
            let filename = 'documento.pdf'; 

            // Extrai o nome do arquivo enviado pelo back-end (que agora já vem formatado como remuneracao_YYYY_MM.pdf)
            const disposition = response.headers.get('content-disposition') || response.headers.get('Content-Disposition');
            
            if (disposition && disposition.toLowerCase().includes('attachment')) {
                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            return { blob, filename };
            
        } catch (error: unknown) {
            if (error instanceof FetchRequestError && error.status === 401) {
                throw new Error('Sessão expirada ou não autorizada. Faça login novamente.');
            }
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                throw new ConnectionError();
            }
            throw error;
        }
    }


    /**
     * Realiza uma requisição POST voltada para o upload de arquivos (Multipart/Form-Data).
     * @param input A URL ou objeto RequestInfo para a requisição.
     * @param formData O objeto FormData contendo o arquivo e os parâmetros.
     * @param config Configuração contendo os cabeçalhos customizados (ex: jwtPortal).
     */
    static async upload(input: RequestInfo | URL, formData: FormData, config: IRequestConfig): Promise<void> {
        // Importante: NÃO podemos deixar o Content-Type como application/json aqui
        const headers = new Headers(config.getHeaders());
        
        const init: RequestInit = { 
            method: HTTPMethod.POST, 
            headers, 
            body: formData // O próprio navegador injeta o Content-Type correto com o boundary
        };

        try {
            const response = await fetch(input, init);

            if (!response.ok) {
                const errorBody = await response.text();
                throw new FetchRequestError(response.status, errorBody);
            }
            
            // Como o back-end retorna 201 vazio, não tentamos parsear JSON. Retornamos com sucesso.
            return;
            
        } catch (error: unknown) {
            if (error instanceof FetchRequestError && error.status === 401) {
                throw new Error('Sessão inválida ou não autorizada no Portal.');
            }
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                throw new ConnectionError();
            }
            throw error;
        }
    }

    /**
     * Realiza uma requisição DELETE por ID que não retorna corpo (Void).
     */
    static async delete(input: RequestInfo | URL, config: IRequestConfig): Promise<void> {
        return await this.fetchVoid(HTTPMethod.DELETE, input, null, config);
    }

    /**
     * Uma utilidade genérica para requisições que retornam HTTP Status de sucesso, 
     * mas com o corpo vazio (Void), impedindo erros de parse de JSON.
     */
    static async fetchVoid(method: HTTPMethod, input: RequestInfo | URL, body?: BodyInit | null, config?: IRequestConfig): Promise<void> {
        const headers = new Headers(config?.getHeaders() ?? new DefaultRequestConfig().getHeaders());
        const init: RequestInit = { method, headers, ...(body ? { body } : {}) };

        try {
            const response = await fetch(input, init);

            if (!response.ok) {
                const errorBody = await response.text();
                throw new FetchRequestError(response.status, errorBody);
            }
            
            return; // Sucesso absoluto, retorna sem tentar parsear JSON
        } catch (error: unknown) {
            if (error instanceof FetchRequestError && error.status === 401) {
                throw new Error('Sessão expirada ou não autorizada no Portal.');
            }
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                throw new ConnectionError();
            }
            throw error;
        }
    }
}